/**
 * The affiliate program: the visitor's half (backend/api/api/services/affiliates.py).
 *
 * An affiliate's link is any page with ?via=<code>. The code is kept here from the
 * first visit, because sign-up leaves the page (Clerk's form, Google and back) and a
 * redirect can drop the query. The first code a browser sees wins for LINK_DAYS, the
 * same window the API holds the claim to; after that the next link may take its place.
 * It is kept apart from the share-a-video referral (growth/referral.js): one visitor
 * can carry both.
 *
 * It is sent to our own API once there is an account (AffiliateClaim), with the time
 * of that first visit, and deleted when the API has answered either way.
 */
import { intlTag } from '../i18n/locales';
import { PAGES } from '../i18n/routes';

const KEY = 'cs_via';
export const LINK_DAYS = 60;
const MAX_AGE_MS = LINK_DAYS * 24 * 60 * 60 * 1000;
const CODE = /^[a-z0-9]{3,32}$/i;

const storage = () => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

/** The affiliate code a link carries (?via=<code>), lower-cased, or null. */
export function affiliateFromSearch(search) {
  const code = new URLSearchParams(search || '').get('via');
  return code && CODE.test(code.trim()) ? code.trim().toLowerCase() : null;
}

/** The code this browser still has to claim and when it first arrived with it:
 *  { code, firstSeenAt } (ms), or null. An expired or unreadable one is dropped. */
export function pendingAffiliate(now = Date.now()) {
  const store = storage();
  if (!store) return null;
  try {
    const saved = JSON.parse(store.getItem(KEY) || 'null');
    if (!saved?.code || !CODE.test(saved.code) || !(now - saved.at <= MAX_AGE_MS)) {
      store.removeItem(KEY);
      return null;
    }
    return { code: saved.code, firstSeenAt: saved.at };
  } catch {
    return null;
  }
}

/** Keep the code a link carried, unless an earlier one is still in its window: the
 *  first link wins. Returns true when this call stored it (a first visit). */
export function rememberAffiliate(code, now = Date.now()) {
  const store = storage();
  if (!store || !code || !CODE.test(code)) return false;
  if (pendingAffiliate(now)) return false;
  try {
    store.setItem(KEY, JSON.stringify({ code: code.toLowerCase(), at: now }));
    return true;
  } catch {
    // Storage full or blocked: the ?via= in the link is still there for this page
    return false;
  }
}

export function forgetAffiliate() {
  try {
    storage()?.removeItem(KEY);
  } catch {
    // nothing to forget
  }
}

const SITE = 'https://www.clip-short.com';
// Currencies Stripe counts in whole units, so an amount in them is not cents
const ZERO_DECIMAL = new Set(['bif', 'clp', 'djf', 'gnf', 'jpy', 'kmf', 'krw', 'mga', 'pyg', 'rwf', 'ugx', 'vnd', 'vuv', 'xaf', 'xof', 'xpf']);

/** An affiliate's link: the home page in the language they read the app in. */
export const affiliateLink = (code, locale = 'en') => `${SITE}${PAGES.home[locale] || '/'}?via=${code}`;

/** An amount as Stripe counts it (cents, or whole units), written for the reader. */
export function formatMoney(minor, currency, locale = 'en') {
  const code = (currency || 'usd').toLowerCase();
  const amount = ZERO_DECIMAL.has(code) ? minor : minor / 100;
  try {
    return new Intl.NumberFormat(intlTag(locale), { style: 'currency', currency: code.toUpperCase() }).format(amount);
  } catch {
    return `${amount} ${code.toUpperCase()}`;
  }
}
